import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { blogPosts } from "@/data/content";
import useSeo from "@/hooks/useSeo";

export default function Blog() {
    useSeo({
        title: "News & Field Notes — Logistics Operating Playbooks",
        description:
            "Operating playbooks, case studies and field notes from the Beeta Logistics team — auto inbound, warehousing, route optimisation and ePOD.",
        path: "/blog",
    });
    return (
        <div data-testid="blog-page" className="bg-white">
            <section className="relative pt-16 pb-20 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5">
                        News & Field Notes
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95] max-w-4xl">
                        Notes from the dock,
                        <br />
                        the road and the warehouse.
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl">
                        Operating playbooks, case studies and occasional opinions from the
                        Beeta team.
                    </p>
                </div>
            </section>

            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Featured */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link
                            to={`/blog/${blogPosts[0].slug}`}
                            data-testid="blog-featured"
                            className="group grid lg:grid-cols-12 gap-8 items-stretch rounded-3xl overflow-hidden border border-slate-200 hover:shadow-2xl transition-all"
                        >
                            <div className="lg:col-span-7 aspect-[16/10] overflow-hidden">
                                <img
                                    src={blogPosts[0].cover}
                                    alt={blogPosts[0].title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-xs font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full bg-blue-50 text-blue-700">
                                        Featured
                                    </span>
                                    <span className="text-xs font-semibold text-slate-500">
                                        {blogPosts[0].category} · {blogPosts[0].readTime}
                                    </span>
                                </div>
                                <h2 className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter leading-tight mb-4 group-hover:text-blue-600 transition-colors">
                                    {blogPosts[0].title}
                                </h2>
                                <p className="text-slate-600 leading-relaxed">
                                    {blogPosts[0].excerpt}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-slate-900 font-semibold group-hover:gap-3 transition-all">
                                    {blogPosts[0].date}
                                    <ArrowRight className="w-4 h-4" />
                                </div>
                            </div>
                        </Link>
                    </motion.div>

                    {/* Rest */}
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {blogPosts.slice(1).map((post, i) => (
                            <motion.article
                                key={post.slug}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="group"
                            >
                                <Link
                                    to={`/blog/${post.slug}`}
                                    data-testid={`blog-card-${post.slug}`}
                                    className="block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all"
                                >
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img
                                            src={post.cover}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <div className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600 mb-3">
                                            {post.category} · {post.readTime}
                                        </div>
                                        <h3 className="font-display text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors leading-tight">
                                            {post.title}
                                        </h3>
                                        <p className="text-sm text-slate-600 leading-relaxed">
                                            {post.excerpt}
                                        </p>
                                        <div className="mt-5 text-xs text-slate-500 font-semibold">
                                            {post.date}
                                        </div>
                                    </div>
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
